/**
 * Token de foyer côté client.
 *
 * Le token arrive dans le **fragment** de l'URL (`#t=…`), jamais dans la query
 * string : un fragment n'est pas transmis au serveur, donc il n'apparaît ni
 * dans les logs Cloudflare, ni dans un en-tête `Referer` vers un tiers.
 *
 * Dès sa lecture, il est rangé en `localStorage` et **effacé de la barre
 * d'adresse** — sans quoi il resterait visible, partageable par erreur, et
 * conservé dans l'historique du navigateur.
 */

const TOKEN_KEY = 'todofamille.token';

/**
 * Récupère le token : depuis le fragment s'il y en a un, sinon depuis le
 * stockage. Un fragment présent l'emporte toujours, ce qui permet de basculer
 * de foyer en rouvrant simplement un autre lien.
 */
export function claimToken(): string | null {
  const fromUrl = readFragmentToken();

  if (fromUrl) {
    localStorage.setItem(TOKEN_KEY, fromUrl);
    stripFragment();
    return fromUrl;
  }

  return localStorage.getItem(TOKEN_KEY);
}

export function currentToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function forgetToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Reconstruit le lien de partage.
 *
 * Affiché dans les réglages : c'est le seul moyen de repartager l'accès si
 * l'autre appareil a perdu ses données de site. Sans cela, un effacement de
 * cache obligerait à refrapper un lien depuis un poste de développement.
 */
export function shareLink(): string | null {
  const token = currentToken();
  return token ? `${window.location.origin}/#t=${token}` : null;
}

/** Le token précédait-il ce lancement ? Sert à distinguer un premier accès. */
export function hasStoredToken(): boolean {
  return localStorage.getItem(TOKEN_KEY) !== null;
}

/**
 * Lit l'identifiant de foyer contenu dans le token.
 *
 * Le payload est en clair (signé, pas chiffré) : le client peut le lire sans
 * appeler le serveur, ce qui lui permet de créer des lignes localement dès le
 * premier lancement, avant toute synchronisation — donc hors ligne.
 *
 * Aucune confiance n'est accordée à cette lecture : c'est le serveur qui vérifie
 * la signature. Une valeur bricolée ici ne donnerait accès à rien.
 */
export function householdIdFromToken(token: string): string | null {
  const payload = token.split('.')[0];
  if (!payload) return null;

  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(json) as { h?: unknown };

    return typeof decoded.h === 'string' ? decoded.h : null;
  } catch {
    return null;
  }
}

/**
 * Extrait un token d'une saisie libre.
 *
 * Accepte aussi bien le lien complet que le token seul : sur iOS, le lien est
 * collé à la main dans l'application installée (cf. `storeToken`), et un
 * copier-coller depuis une messagerie ramène souvent des espaces ou un
 * retour à la ligne.
 */
export function extractToken(input: string): string | null {
  const cleaned = input.trim();
  if (cleaned.length === 0) return null;

  // Lien complet : on lit le fragment.
  const hash = cleaned.indexOf('#');
  if (hash >= 0) {
    const value = new URLSearchParams(cleaned.slice(hash + 1)).get('t');
    return value && isTokenShaped(value) ? value : null;
  }

  return isTokenShaped(cleaned) ? cleaned : null;
}

/** Enregistre un token saisi manuellement. */
export function storeToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** Forme attendue : `<payload>.<signature>`, en base64url. */
function isTokenShaped(value: string): boolean {
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value);
}

function readFragmentToken(): string | null {
  const raw = window.location.hash.replace(/^#/, '');
  if (!raw) return null;

  const value = new URLSearchParams(raw).get('t');
  return value && isTokenShaped(value) ? value : null;
}

function stripFragment(): void {
  const { pathname, search } = window.location;
  window.history.replaceState(null, '', `${pathname}${search}`);
}
